import { useCallback } from 'react';
import { useComponent } from '../state/ComponentContext';
import { useConfigentoStoreRef } from '../state/StoreContext';
import { useRuleResults } from '../rules/RuleResultsContext';
import { useTranslation } from '../i18n';
import type { Group } from '../schema';
import {
    validateCharacteristics,
    validateConfiguration,
    type ValidationError,
} from './validate';

/**
 * Returns a function that runs validation against the current store snapshot
 * and returns an array of errors. Pure on call — no React state changes —
 * so callers (ActionButton click, Wizard "next") wire their own response.
 */
export function useRunValidation(): () => ValidationError[] {
    const component = useComponent();
    const store = useConfigentoStoreRef();
    const rules = useRuleResults();
    const { t, locale } = useTranslation();

    return useCallback(() => {
        const selections = store.getState().selections;
        return validateConfiguration(component, selections, rules, t, locale);
    }, [component, store, rules, t, locale]);
}

/**
 * Step-scoped variant for the Wizard's "Weiter" gate. Validates only the
 * Characteristics of the given Group so the user can't skip required fields
 * on the active step.
 */
export function useRunGroupValidation(): (group: Group) => ValidationError[] {
    const store = useConfigentoStoreRef();
    const rules = useRuleResults();
    const { t, locale } = useTranslation();

    return useCallback(
        (group: Group) => {
            const selections = store.getState().selections;
            return validateCharacteristics(
                group.characteristics,
                selections,
                rules,
                t,
                locale,
            );
        },
        [store, rules, t, locale],
    );
}
